import { defineStore } from 'pinia'
import {
  type VNode,
  type WatchStopHandle,
  h,
  ref,
  watchEffect,
  nextTick
} from 'vue'
import {
  type RouteComponent,
  type RouteLocationNormalizedLoaded,
  useRoute,
  useRouter
} from 'vue-router'
import { whiteList } from '@/permission'

interface RouterMap {
  /** 标题 */
  title: string
  /** 路由地址 */
  path: string
  /** 路由 */
  route: RouteLocationNormalizedLoaded,
  /** 当前路由的组件 */
  component: RouteComponent,
  /** 当前路由的唯一标识 */
  key: string,
  /** 组件 */
  node: VNode
}

interface OperateParams {
  /** 用来替换的标题 */
  title?: string,
  /** 操作的路由地址 */
  path?: string
}

export const enum HISTORY_TAB_TYPE {
  /** 删除历史页签 */
  CLOSE = 1,
  /** 关闭其它的历史页签 */
  CLOSE_OTHER = 2,
  /** 关闭左侧历史页签 */
  CLOSE_LEFT = 3,
  /** 关闭右侧历史页签 */
  CLOSE_RIGHT = 4,
  /** 刷新历史页签 */
  REFRESH = 5,
  /** 替换标题 */
  REPLACE_TITLE = 6
}

/** 历史页签 */
export const useHistoryTab = defineStore('historyBar', () => {

  const getKey = () => Math.random().toString(32).slice(2)

  let unWatch = false
  /** 存储路由实例 */
  let router: ReturnType<typeof useRouter>
  /** 阻止监听 */
  let watchStopHandle: WatchStopHandle
  /** 存储路径与标题的映射关系 */
  const titleMap = new Map<string, string>([
    [ '/index', '首页' ]
  ])
  /** 当前历史页签列表 */
  const tabList = ref<RouterMap[]>([])
  /** 存储历史页签与索引的映射 */
  const tabIndexMap = new Map<string, number>

  /** 获取当前路由地址 */
  const getCurrentPath = () => router.currentRoute.value.fullPath

  function operateFactory(operateType: HISTORY_TAB_TYPE, params: OperateParams) {
    let { path, title } = params
    const currentPagePath = getCurrentPath()
    path = path || currentPagePath
    const index = tabIndexMap.get(path)
    if (index === undefined) return
    switch (operateType) {
      case HISTORY_TAB_TYPE.CLOSE:
        if (tabList.value.length === 1) return
        tabList.value.splice(index, 1)
        break
      case HISTORY_TAB_TYPE.CLOSE_LEFT:
        tabList.value.splice(0, index)
        break
      case HISTORY_TAB_TYPE.CLOSE_OTHER:
        tabList.value = [ tabList.value[index] ]
        break
      case HISTORY_TAB_TYPE.CLOSE_RIGHT:
        tabList.value.splice(index + 1, tabList.value.length)
        break
      case HISTORY_TAB_TYPE.REFRESH:
        tabList.value[index].key = getKey()
        break
      case HISTORY_TAB_TYPE.REPLACE_TITLE:
        tabList.value[index].title = title!
        return
      default: return
    }
    // 重设索引关系
    tabIndexMap.clear()
    // 判断当前激活的路由是不是还在路由栈中
    let currentRouterAtTabs = false
    for (let i = 0; i < tabList.value.length; i++) {
      const currentIndexPath = tabList.value[i].path
      if (currentIndexPath === currentPagePath) {
        currentRouterAtTabs = true
      }
      tabIndexMap.set(currentIndexPath, i)
    }
    // 重设路由, 解决触发watchEffect的时候，路由还没有更新问题
    if (!currentRouterAtTabs) {
      unWatch = true
      nextTick(() => {
        tabList.value[0] && router.replace(tabList.value[0].path)
        unWatch = false
      })
    }
  }

  function isNoPushStack(path: string) {
    return whiteList.some(t => path.startsWith(t))
  }

  return new class {

    /** 历史页签 */
    tabList = tabList

    /** 监听路由变化 */
    listenRouter = () => {
      router = useRouter()
      const route = useRoute()

      if (typeof watchStopHandle === 'function') {
        tabList.value.splice(0, tabList.value.length)
        watchStopHandle()
      }

      watchStopHandle = watchEffect(() => {
        const { matched } = router.currentRoute.value
        const matchedRoute = matched[matched.length - 1]
        if (!matchedRoute) return null
        const currentRoute = { ...route }
        if (unWatch) return
        const path = currentRoute.fullPath,
          // @ts-ignore 一定会有
          component = matchedRoute.components.default
        // @ts-ignore 一定会有
        component['inheritAttrs'] = false
        if (isNoPushStack(path)) return
        if (!tabIndexMap.has(path)) {
          tabIndexMap.set(
            path,
            tabList.value.push({
              path,
              component,
              key:   getKey(),
              title: titleMap.get(path) || path,
              route: currentRoute,
              node:  h(component, { route })
            }) - 1
          )
        }
      })
    }

    /**
     * 记录菜单标题与路径
     * @param path 
     * @param title 
     */
    recordMenuInfo = titleMap.set.bind(titleMap)

    /** 刷新历史页签 */
    onRefresh = (path?: string) => operateFactory(HISTORY_TAB_TYPE.REFRESH, { path })

    /** 关闭当前历史页签 */
    onClose = (path?: string) => operateFactory(HISTORY_TAB_TYPE.CLOSE, { path })

    /** 关闭其它历史页签 */
    onCloseOther = (path?: string) => operateFactory(HISTORY_TAB_TYPE.CLOSE_OTHER, { path })

    /** 关闭左侧历史页签 */
    onCloseLeft = (path?: string) => operateFactory(HISTORY_TAB_TYPE.CLOSE_LEFT, { path })

    /** 关闭右侧历史页签 */
    onCloseRight = (path?: string) => operateFactory(HISTORY_TAB_TYPE.CLOSE_RIGHT, { path })

    /** 替换标题 */
    replaceTitle = (params: { title: string, path?: string }) => operateFactory(HISTORY_TAB_TYPE.REPLACE_TITLE, params)

  }

})